import { useState, useEffect, useCallback, useRef } from 'react';
import type { BreakpointDef } from '../../Config/config';

// Hook optimisé pour gérer les breakpoints avec debouncing
export function useBreakpoint(breakpoints: BreakpointDef[], debounceMs: number = 150) {
  const sorted = [...breakpoints].sort((a, b) => a.minWidth - b.minWidth);
  const [currentBreakpoint, setCurrentBreakpoint] = useState<BreakpointDef>(sorted[0]);
  const timeoutRef = useRef<number | undefined>(undefined);
  const isUpdatingRef = useRef(false);

  const updateBreakpoint = useCallback(() => {
    if (isUpdatingRef.current) return; // Éviter les mises à jour simultanées
    
    isUpdatingRef.current = true;
    const width = window.innerWidth;
    let current = sorted[0];
    
    for (const bp of sorted) {
      if (width >= bp.minWidth) current = bp;
    }
    
    setCurrentBreakpoint(current);
    
    // Reset le flag après un court délai
    setTimeout(() => {
      isUpdatingRef.current = false;
    }, 50);
  }, [sorted]);

  const debouncedUpdateBreakpoint = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(updateBreakpoint, debounceMs);
  }, [updateBreakpoint, debounceMs]);

  useEffect(() => {
    updateBreakpoint(); // Initial call
    
    window.addEventListener('resize', debouncedUpdateBreakpoint, { passive: true });
    
    return () => {
      window.removeEventListener('resize', debouncedUpdateBreakpoint);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [debouncedUpdateBreakpoint, updateBreakpoint]);

  return currentBreakpoint;
}

// Hook pour simuler des breakpoints (pour tester des grilles plus petites)
export function useSimulatedBreakpoint(
  breakpoints: BreakpointDef[], 
  simulatedWidth?: number
) {
  const sorted = [...breakpoints].sort((a, b) => a.minWidth - b.minWidth);
  const [currentBreakpoint, setCurrentBreakpoint] = useState<BreakpointDef>(sorted[0]);
  const [isSimulating, setIsSimulating] = useState(false);

  const updateBreakpoint = useCallback((width: number) => {
    let current = sorted[0];
    
    for (const bp of sorted) {
      if (width >= bp.minWidth) current = bp;
    }
    
    setCurrentBreakpoint(current);
  }, [sorted]);

  useEffect(() => {
    if (simulatedWidth !== undefined) {
      setIsSimulating(true);
      updateBreakpoint(simulatedWidth);
    } else {
      setIsSimulating(false);
      updateBreakpoint(window.innerWidth);
    }
  }, [simulatedWidth, updateBreakpoint]);

  return { currentBreakpoint, isSimulating };
}
